import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { supabase } from '../lib/supabase';

export const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '/api';
/** Browser data access is Supabase-native; the legacy backend client is retained
 * only to keep unconverted optional integrations from issuing network calls. */
export const SUPABASE_ONLY = true;

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request Interceptor: Inject JWT token from Supabase session or persistent storage
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (SUPABASE_ONLY) {
      return Promise.reject(new Error('The browser API is Supabase-native. Use a module in src/api/supabase instead.'));
    }
    try {
      let token: string | null = null;
      if (supabase) {
        const { data } = await supabase.auth.getSession();
        token = data?.session?.access_token || null;
      }
      if (!token && typeof window !== 'undefined') {
        token = window.sessionStorage.getItem('compass_token') ||
                window.localStorage.getItem('compass_token') ||
                window.sessionStorage.getItem('compass_auth_token');
      }
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      console.warn('API Client: Could not acquire session token', e);
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response Interceptor: Uniform error formatting & safe 401 session refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ message?: string; error?: string }>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;
    const message = error.response?.data?.message || error.response?.data?.error || error.message;

    // Handle 401 Unauthorized with safe session refresh
    if (status === 401 && originalRequest && !originalRequest._retry) {
      const isCreateTicket = 
        originalRequest.method?.toUpperCase() === 'POST' && 
        (originalRequest.url === '/tickets' || originalRequest.url?.endsWith('/tickets'));

      // Do NOT automatically retry POST ticket creation to prevent duplicate tickets
      if (!isCreateTicket && supabase) {
        originalRequest._retry = true;
        try {
          const { data: refreshData, error: refreshErr } = await supabase.auth.refreshSession();
          const newToken = refreshData?.session?.access_token;
          if (!refreshErr && newToken) {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return apiClient(originalRequest);
          }
        } catch (refErr) {
          console.warn('API Client: Session token refresh failed', refErr);
        }
      }

      console.warn('API Client: Unauthorized access (401). Session may be expired.');
    }

    const appError: any = new Error(message || 'An unexpected API error occurred');
    appError.response = error.response;
    appError.status = status;
    return Promise.reject(appError);
  }
);

export default apiClient;
